import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Droplets, 
  Factory, 
  Layers, 
  Settings, 
  Wrench,
  ChevronRight,
  ArrowRight 
} from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { ROUTE_PATHS } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function ProductionEn() {
  const [activeSection, setActiveSection] = useState("foundry");

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navigationEn = [
    { id: "foundry", label: "Foundry", icon: <Droplets className="w-4 h-4" /> },
    { id: "mold-manufacturing", label: "Mold Manufacturing", icon: <Wrench className="w-4 h-4" /> },
    { id: "extrusion", label: "Extrusion", icon: <Factory className="w-4 h-4" /> },
    { id: "surface-treatment", label: "Surface Treatment", icon: <Layers className="w-4 h-4" /> },
    { id: "mechanical", label: "Mechanical Processing", icon: <Settings className="w-4 h-4" /> }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={IMAGES.FACTORY_FLOOR_1}
            alt="Production" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl font-bold mb-6"
          >
            Production Capabilities
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Comprehensive aluminum solutions from raw material to finished product with our integrated production facilities.
          </motion.p>
        </div>
      </section>

      {/* Navigation */}
      <nav className="sticky top-24 z-40 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-center space-x-8 py-4">
            {navigationEn.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors cursor-pointer"
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Foundry Section */}
      <section id="foundry" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Droplets className="w-4 h-4" />
                Quality Starting from Raw Material
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Foundry Operations</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Our modern foundry facility is the first and most critical link in our production chain. 
                We produce international standard 6000 and 7000 series alloy billets by blending recycled aluminum and primary ingots.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "15,000 Tons Annual Billet Casting",
                  "Precision Spectrometer Analysis",
                  "Homogenization Furnaces",
                  "Hot Cutting Billet Preparation"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video group"
              >
                <img 
                  src={IMAGES.ALUMINUM_BILLET_CASTING_PLANT_1_302} 
                  alt="Aluminum Billet Casting Plant" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold mb-1">Billet Casting Plant</h3>
                  <p className="text-sm opacity-90">Modern casting technology</p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video group"
              >
                <img 
                  src={IMAGES.INDIR_2_303} 
                  alt="Casting Process Operations" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg font-semibold mb-1">Process Operations</h3>
                  <p className="text-sm opacity-90">Quality control stages</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mold Manufacturing Section */}
      <section id="mold-manufacturing" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Wrench className="w-4 h-4" />
                Precision Mold Technology
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Mold Manufacturing</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                With years of experience, we carry out all processes from design to manufacturing of extrusion molds 
                in-house. Precision mold production with CAD/CAM technologies.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "3D Design and Simulation",
                  "CNC Precision Manufacturing",
                  "Quality Control Systems",
                  "H13 Steel Mold Material"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video lg:aspect-square"
            >
              <img src={IMAGES.EKRAN_G_R_NT_S_2026_02_10_172309_276} alt="Mold Manufacturing" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Extrusion Section */}
      <section id="extrusion" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Factory className="w-4 h-4" />
                Advanced Extrusion Technology
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Extrusion Lines</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Our modern extrusion lines ranging from 4" to 8" enable us to produce profiles in various sizes and complexities. 
                We serve architectural and industrial applications with high precision and quality.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "4\" - 8\" Extrusion Line Range",
                  "Complex Profile Capability",
                  "Precision Tolerance Control",
                  "High Production Capacity"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video lg:aspect-square"
            >
              <img src={IMAGES.EKRAN_G_R_NT_S_2026_02_07_202330_81} alt="Extrusion" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Surface Treatment Section */}
      <section id="surface-treatment" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Layers className="w-4 h-4" />
                Advanced Surface Solutions
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Surface Treatment</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Our anodizing and electrostatic powder coating facilities provide durable and aesthetic surface solutions. 
                We offer various color and texture options to meet customer requirements.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Anodizing Facilities",
                  "Powder Coating Lines",
                  "Color Variety",
                  "Durability Testing"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video lg:aspect-square"
            >
              <img src={IMAGES.BOYAHANE_KONVEYOR_SISTEMLERI_ZHJL0TVUMW2ULU89TL8U_86} alt="Surface Treatment" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mechanical Processing Section */}
      <section id="mechanical" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                <Settings className="w-4 h-4" />
                Precision Manufacturing
              </div>
              <h2 className="text-4xl font-bold mb-6 text-primary">Mechanical Processing</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Our mechanical processing department provides precision cutting, drilling, and CNC machining solutions. 
                We deliver ready-to-use products according to customer specifications.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "CNC Machining Centers",
                  "Precision Cutting",
                  "Drilling Operations",
                  "Custom Processing"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-slate-700 font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video lg:aspect-square"
            >
              <img src={IMAGES.RETIM_HATTI_82} alt="Mechanical Processing" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Start Your Production?
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90">
              Contact our expert team for your aluminum production needs. We provide customized solutions for your projects.
            </p>
            <Button asChild size="lg" variant="secondary" className="text-lg px-8 py-6">
              <Link to={ROUTE_PATHS.CONTACT_EN}>
                Request Quote and Technical Support <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}